import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Search, Moon, Sun, Globe, ChevronDown, LogOut, Plus, User } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Topbar() {
  const { t, lang, setLang, theme, setTheme, cases, currentCase, setCurrentCaseId } = useApp();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!q || q.length < 2 || !currentCase) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/cases/${currentCase.id}/search`, { params: { q } });
        setResults(data.results || []);
        setOpen(true);
      } catch {}
    }, 250);
  }, [q, currentCase]);

  const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header
      data-testid="app-topbar"
      className="h-16 border-b border-border/70 px-5 lg:px-7 flex items-center gap-4 glass shrink-0 z-30"
    >
      {/* Case switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="case-switcher"
            className="group flex items-center gap-2.5 px-3 py-1.5 rounded-md border border-border/80 hover:border-[hsl(var(--gold)/0.4)] transition-all text-left max-w-[300px]"
          >
            <div className="size-2 rounded-full bg-[hsl(var(--gold))] animate-pulse-soft" />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-mono">{t("cases")}</div>
              <div className="text-[13px] font-medium truncate">{currentCase?.title || t("select_case")}</div>
            </div>
            <ChevronDown className="size-4 text-muted-foreground group-hover:gold-text" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[320px]" align="start">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("cases")}</DropdownMenuLabel>
          {cases.map(c => (
            <DropdownMenuItem
              key={c.id} data-testid={`case-option-${c.id}`}
              onClick={() => setCurrentCaseId(c.id)}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <span className="font-medium text-[13px]">{c.title}</span>
              <span className="text-[11px] text-muted-foreground font-mono">{c.case_number || c.client_name}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate("/onboarding")} data-testid="new-case-action">
            <Plus className="size-4 mr-2" /> {t("new_case")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Search */}
      <div className="relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" strokeWidth={1.5} />
        <input
          data-testid="global-search-input"
          value={q} onChange={e => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t("search") + " στην υπόθεση…"}
          className="w-full bg-muted/40 border border-border/70 rounded-md pl-9 pr-3 py-2 text-[13px] outline-none focus:border-[hsl(var(--gold)/0.5)] transition-colors"
        />
        {open && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-2 max-h-[420px] overflow-y-auto scrollbar-thin glass border border-border/70 rounded-lg shadow-xl z-50">
            {results.map((r, i) => (
              <div key={i}
                className="px-4 py-2.5 border-b border-border/40 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
                data-testid={`search-result-${i}`}
              >
                <div className="text-[10px] uppercase tracking-wider gold-text font-mono">{r.type}</div>
                <div className="text-[13px] font-medium truncate">{r.data.title || r.data.full_name || r.data.purpose || r.data.evidence_id}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lang toggle */}
      <button
        data-testid="lang-toggle"
        onClick={() => setLang(lang === "el" ? "en" : "el")}
        className="size-9 flex items-center justify-center rounded-md border border-border/70 hover:border-[hsl(var(--gold)/0.4)] transition-colors text-[11px] font-mono uppercase tracking-wider"
        title={t("language")}
      >
        <Globe className="size-4 mr-1" strokeWidth={1.5}/>{lang}
      </button>

      {/* Theme toggle */}
      <button
        data-testid="theme-toggle"
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
        className="size-9 flex items-center justify-center rounded-md border border-border/70 hover:border-[hsl(var(--gold)/0.4)] transition-colors"
        title={theme === "dark" ? t("light_mode") : t("dark_mode")}
      >
        {theme === "dark" ? <Sun className="size-4 gold-text" strokeWidth={1.5}/> : <Moon className="size-4" strokeWidth={1.5}/>}
      </button>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu" className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md border border-border/70 hover:border-[hsl(var(--gold)/0.4)] transition-colors">
            <div className="size-7 rounded-full bg-[hsl(var(--gold)/0.18)] gold-text flex items-center justify-center text-[11px] font-bold font-heading">{initials}</div>
            <div className="text-left hidden sm:block">
              <div className="text-[12px] font-medium leading-tight">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground font-mono">{user?.role}</div>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/settings")}>
            <User className="size-4 mr-2" />{t("settings")}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }}>
            <LogOut className="size-4 mr-2" />{t("logout")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
